import { InKindDonation, ResourceMeta } from "../../types"
import { Fund } from "source/giving/donate/form/types"
import BlankState from "./BlankState"
import InKindDonationDetails from "./InKindDonationDetails"
import pluralize from "source/shared/pluralize"
import { AlertMessage, Loading } from "source/shared/components"
import { useSuspenseInfiniteQuery } from "@tanstack/react-query"
import { fetchInKindDonations } from "../../queries"
import InfiniteScroll from "react-infinite-scroller"
import { useDonationHistoryContext } from "../../hooks/useDonationHistoryContext"

interface InKindDonationsListProps {
  donationYears: number[] | undefined
  activeYear: number
  activeFund: Fund | null
  downloadUrl: string
}

export default function InKindDonationsList({
  donationYears,
  activeYear,
  activeFund,
  downloadUrl,
}: InKindDonationsListProps) {
  const { data, isError, fetchNextPage, hasNextPage, isFetching } =
    useSuspenseInfiniteQuery({
      queryKey: ["InKindDonations", activeYear, activeFund || "allFunds"],
      queryFn: ({ pageParam }) => {
        return fetchInKindDonations({
          year: activeYear,
          fund: activeFund,
          pageParam,
        })
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage) => lastPage.meta?.next?.offset,
    })
  const inKindDonations: InKindDonation[] =
    data.pages.flatMap((page) => page.inKindDonations) || []
  const inKindDonationsMeta: ResourceMeta | undefined = data.pages[0]?.meta
  const loadMore = () => !isFetching && fetchNextPage()

  const { showDownloadButton, setShowDownloadButton } =
    useDonationHistoryContext()

  const shouldDisplay = inKindDonations?.length > 0
  if (showDownloadButton !== shouldDisplay) {
    setShowDownloadButton(shouldDisplay)
  }

  return isError ? (
    <AlertMessage type="error" icon="general#exclamation-triangle">
      Something went wrong. Please try again.
    </AlertMessage>
  ) : (
    inKindDonations &&
      (inKindDonations.length === 0 ? (
        <BlankState
          donationType="inKind"
          activeYear={
            donationYears && donationYears.length > 1 ? activeYear : null
          }
          activeFund={activeFund}
        />
      ) : (
        <>
          {activeYear && (
            <div className="d-f fd-c fd-r@sm g-4p g-1@sm ai-fs jc-sb mt-2">
              <div className="fw-600 tt-u">
                {inKindDonationsMeta?.total_count}{" "}
                <span className="c-tint2">
                  {pluralize(
                    inKindDonationsMeta?.total_count,
                    "donation",
                    false,
                  )}{" "}
                  in{" "}
                </span>
                {activeYear}{" "}
                {activeFund && (
                  <>
                    <span className="c-tint2">to</span> {activeFund.name}
                  </>
                )}
              </div>
              <a
                href={downloadUrl}
                className="btn sm-btn outline-btn d-n d-if@sm"
              >
                Download
              </a>
            </div>
          )}
          <InfiniteScroll
            hasMore={hasNextPage}
            loadMore={loadMore}
            loader={<Loading key="loading" />}
          >
            {inKindDonations?.map((donation) => (
              <InKindDonationDetails
                key={donation.id}
                inKindDonation={donation}
              />
            ))}
          </InfiniteScroll>
        </>
      ))
  )
}
